// Auth state slice. Stores the currently-authenticated user and exposes
// login / logout / refresh actions. The cookie session lives in an httpOnly
// cookie set by the server, so this slice only mirrors the public projection
// of the user.

import { create } from 'zustand';

import { getDefaultApiContext } from '../api/client.js';
import {
  loginApi,
  logoutApi,
  meApi,
  type AuthMe,
  type AuthUser,
} from '../api/clients/auth.js';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: AuthMe | null;
  error: string | null;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
}

function meToView(user: AuthMe | AuthUser): AuthMe {
  if ('lastLogin' in user) return user;
  return {
    id: user.id,
    username: user.username,
    role: user.role,
    lastLogin: null,
    tokenSpend: 0,
    lastWorkspace: null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  user: null,
  error: null,
  async refresh(): Promise<void> {
    set({ status: 'loading', error: null });
    const res = await meApi(getDefaultApiContext());
    if (res.ok) {
      set({ status: 'authenticated', user: res.value, error: null });
    } else {
      set({ status: 'anonymous', user: null, error: null });
    }
  },
  async login(username, password) {
    set({ status: 'loading', error: null });
    const res = await loginApi(getDefaultApiContext(), { username, password });
    if (res.ok) {
      set({ status: 'authenticated', user: meToView(res.value), error: null });
      return { ok: true };
    }
    set({ status: 'anonymous', user: null, error: res.error.message });
    return { ok: false, error: res.error.message };
  },
  async logout() {
    await logoutApi(getDefaultApiContext());
    set({ status: 'anonymous', user: null, error: null });
  },
}));
